/**
 * ElevenLabs Pronunciation Dictionary Service
 *
 * Manages creation and caching of ElevenLabs Pronunciation Dictionaries
 * from per-persona pronunciation rules. Dictionaries are created lazily
 * when a TTS session starts and cached for reuse until rules change.
 */

import type { PronunciationRule, PronunciationDictionaryRef } from '@/stores/personaStore'

const ELEVENLABS_API_BASE = 'https://api.elevenlabs.io/v1'

/** ElevenLabs API rule format (snake_case) */
interface ElevenLabsAliasRule {
  type: 'alias'
  string_to_replace: string
  alias: string
  case_sensitive: boolean
  word_boundaries: boolean
}

interface ElevenLabsPhonemeRule {
  type: 'phoneme'
  string_to_replace: string
  phoneme: string
  alphabet: 'ipa' | 'cmu'
  case_sensitive: boolean
  word_boundaries: boolean
}

type ElevenLabsRule = ElevenLabsAliasRule | ElevenLabsPhonemeRule

/** Response from create dictionary API */
interface CreateDictionaryResponse {
  id: string
  name: string
  version_id: string
  version_rules_num: number
  created_by: string
  creation_time_unix: number
  description: string | null
  permission_on_resource: string | null
}

/** Locator format expected by TTS APIs */
export interface PronunciationDictionaryLocator {
  pronunciation_dictionary_id: string
  version_id: string
}

/**
 * Convert frontend pronunciation rules to ElevenLabs API format.
 *
 * Rules default to case-insensitive matching and word-boundary matching.
 * Case-insensitive is the safer default: users typically want "RAG" to match
 * "rag", "Rag", and "RAG" in LLM output regardless of capitalization.
 */
export function rulesToApiFormat(rules: PronunciationRule[]): ElevenLabsRule[] {
  return rules
    .filter(r => r.stringToReplace.trim())
    .map(r => {
      if (r.type === 'phoneme' && r.phoneme) {
        return {
          type: 'phoneme' as const,
          string_to_replace: r.stringToReplace,
          phoneme: r.phoneme,
          alphabet: r.alphabet || 'ipa',
          case_sensitive: false,
          word_boundaries: true,
        }
      }
      return {
        type: 'alias' as const,
        string_to_replace: r.stringToReplace,
        alias: r.alias || r.stringToReplace,
        case_sensitive: false,
        word_boundaries: true,
      }
    })
}

/**
 * Serialize one API-format rule with sorted keys and compact separators.
 * Matches Python json.dumps(rule, sort_keys=True, ensure_ascii=False,
 * separators=(",", ":")). The replacer array both filters and orders keys.
 */
function canonicalRuleJson(rule: ElevenLabsRule): string {
  const keys = Object.keys(rule).sort()
  return JSON.stringify(rule, keys)
}

/**
 * Compute a deterministic, locale-independent hash of pronunciation rules for
 * change detection. MUST stay byte-identical to the backend
 * (_canonical_rules_hash in app/services/whatsapp/pronunciation.py) so a persona
 * used in both the browser and WhatsApp shares one ElevenLabs dictionary.
 *
 * Canonical form: API-format rules -> per-rule sorted-key compact JSON ->
 * sort the rule strings -> "[" + join(",") + "]" -> SHA-256 hex.
 *
 * Note: the djb2 fallback (plain-HTTP dev, no crypto.subtle) hashes the SAME
 * canonical string but with a different digest, so it is out of SHA-256 parity
 * by design — prod is HTTPS and uses SHA-256.
 */
export async function computeRulesHash(rules: PronunciationRule[]): Promise<string> {
  const apiRules = rulesToApiFormat(rules)
  const ruleStrings = apiRules.map(canonicalRuleJson).sort()
  const canonical = '[' + ruleStrings.join(',') + ']'

  if (globalThis.crypto?.subtle) {
    const encoded = new TextEncoder().encode(canonical)
    const hashBuffer = await crypto.subtle.digest('SHA-256', encoded)
    const hashArray = Array.from(new Uint8Array(hashBuffer))
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('')
  }

  // djb2 fallback — change-detection only (not parity, not security)
  let hash = 5381
  for (let i = 0; i < canonical.length; i++) {
    hash = ((hash << 5) + hash + canonical.charCodeAt(i)) >>> 0
  }
  return hash.toString(16).padStart(8, '0')
}

/**
 * Create a new pronunciation dictionary on ElevenLabs from rules.
 */
async function createDictionary(
  apiKey: string,
  name: string,
  rules: PronunciationRule[],
): Promise<{ dictionaryId: string; versionId: string }> {
  const apiRules = rulesToApiFormat(rules)

  const response = await fetch(`${ELEVENLABS_API_BASE}/pronunciation-dictionaries/add-from-rules`, {
    method: 'POST',
    headers: {
      'xi-api-key': apiKey,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      name,
      rules: apiRules,
    }),
  })

  if (!response.ok) {
    const errorText = await response.text().catch(() => response.statusText)
    throw new Error(`Failed to create pronunciation dictionary: ${response.status} ${errorText}`)
  }

  const data: CreateDictionaryResponse = await response.json()
  return {
    dictionaryId: data.id,
    versionId: data.version_id,
  }
}

/**
 * Verify that a cached pronunciation dictionary still exists on ElevenLabs.
 * Returns true if the dictionary is accessible, false if it's gone or the
 * API key doesn't have access to it.
 */
async function verifyDictionaryExists(
  apiKey: string,
  dictionaryId: string,
): Promise<boolean> {
  try {
    const response = await fetch(
      `${ELEVENLABS_API_BASE}/pronunciation-dictionaries/${dictionaryId}`,
      {
        method: 'GET',
        headers: { 'xi-api-key': apiKey },
      },
    )
    return response.ok
  } catch {
    return false
  }
}

/**
 * Ensure a pronunciation dictionary exists and is in sync with the given rules.
 *
 * - If no cached reference exists, creates a new dictionary.
 * - If cached reference exists but rules have changed (hash mismatch), creates a new dictionary.
 * - If cached reference exists and rules match, verifies the dictionary still
 *   exists on ElevenLabs before reusing (guards against stale references from
 *   deleted dictionaries, rotated API keys, etc.).
 *
 * Returns the dictionary reference (for caching) and a locator (for TTS calls).
 */
export async function ensureDictionary(
  apiKey: string,
  personaId: string,
  personaName: string,
  rules: PronunciationRule[],
  existingRef?: PronunciationDictionaryRef,
): Promise<{
  ref: PronunciationDictionaryRef
  locator: PronunciationDictionaryLocator
}> {
  const validRules = rules.filter(r => r.stringToReplace.trim())

  if (validRules.length === 0) {
    throw new Error('No valid pronunciation rules to create dictionary from')
  }

  const currentHash = await computeRulesHash(validRules)

  // If we have a cached reference and rules haven't changed, verify the
  // dictionary still exists on ElevenLabs before reusing it.
  if (existingRef && existingRef.rulesHash === currentHash) {
    const exists = await verifyDictionaryExists(apiKey, existingRef.dictionaryId)
    if (exists) {
      return {
        ref: existingRef,
        locator: {
          pronunciation_dictionary_id: existingRef.dictionaryId,
          version_id: existingRef.versionId,
        },
      }
    }
    // Dictionary was deleted or inaccessible — fall through to recreate
    console.warn(
      '[PronunciationDictionary] Cached dictionary no longer exists on ElevenLabs, recreating:',
      existingRef.dictionaryId,
    )
  }

  // Create a new dictionary (rules changed, no existing dictionary, or stale reference)
  const safeName = personaName.replace(/[^a-zA-Z0-9_-]/g, '_').slice(0, 40)
  const dictName = `persona-${safeName}-${personaId.slice(0, 8)}`
  const { dictionaryId, versionId } = await createDictionary(apiKey, dictName, validRules)

  const ref: PronunciationDictionaryRef = {
    dictionaryId,
    versionId,
    rulesHash: currentHash,
  }

  return {
    ref,
    locator: {
      pronunciation_dictionary_id: dictionaryId,
      version_id: versionId,
    },
  }
}
